import React, { useRef, useState, useEffect } from "react";
import "../../styles/audiobook/Recording.scss";
import useAppTranslation from "../../hooks/useAppTranslation";
import { getLocalizedAssetPath } from "../../utils/getLocalizedLogo";

const Recording = ({
  sentence,
  page,
  total,
  onComplete,
  onConfirm,
  onClose,
  selectedMode,
}) => {
  const { t } = useAppTranslation("components/audiobook/recording");
  const [isRecording, setIsRecording] = useState(false); // 녹음 중인지 판단
  const [hasRecorded, setHasRecorded] = useState(false); // 녹음 완료
  const [recordedBlob, setRecordedBlob] = useState(null); // 완성된 오디오 데이터
  const [audioURL, setAudioURL] = useState(null); // 재생용 URL
  const [isPlaying, setIsPlaying] = useState(false); // 재생 상태
  const [showGuideBox, setShowGuideBox] = useState(true);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);

  const isCustom = selectedMode === "myVoiceWithAI";
  const isLast = isCustom ? true : page + 1 === total;

  console.log("audioURL", audioURL);

  // 페이지 바뀌면 상태 초기화
  useEffect(() => {
    setHasRecorded(false);
    setRecordedBlob(null);
    setIsRecording(false);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [page]);

  console.log("total pages", total);
  console.log("page", page);
  console.log("recordedBlob---->", recordedBlob);
  console.log("isRecording", isRecording);

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });

    const pickSupportedMimeType = () => {
      const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4;codecs=mp4a.40.2",
        "audio/mp4",
        "audio/ogg;codecs=opus",
      ];
      return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || "";
    };

    const chosenType = pickSupportedMimeType();

    mediaRecorderRef.current = new MediaRecorder(
      stream,
      chosenType ? { mimeType: chosenType } : undefined,
    );
    audioChunksRef.current = [];

    mediaRecorderRef.current.ondataavailable = (e) => {
      if (e.data.size > 0) audioChunksRef.current.push(e.data);
    };

    mediaRecorderRef.current.onstop = () => {
      const recType =
        mediaRecorderRef.current?.mimeType ||
        audioChunksRef.current[0]?.type ||
        chosenType ||
        "audio/webm";

      const blob = new Blob(audioChunksRef.current, { type: recType });
      console.log("[DEBUG] recorded blob:", {
        size: blob.size,
        type: blob.type,
      });

      setRecordedBlob(blob);
      setHasRecorded(true);
      setAudioURL(URL.createObjectURL(blob));
    };

    mediaRecorderRef.current.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      const tracks = mediaRecorderRef.current.stream?.getTracks?.();
      tracks?.forEach((t) => t.stop());
    }
    setIsRecording(false);
  };

  const handleRetry = () => {
    setHasRecorded(false);
    setRecordedBlob(null);
    setAudioURL(null);
    setIsPlaying(false);
  };

  const handleSubmit = () => {
    if (recordedBlob) {
      onComplete(recordedBlob);
    }
  };

  const togglePlay = async () => {
    const a = audioRef.current;
    if (!a || !audioURL) {
      console.log("[preview] no audio to play", {
        aExists: !!a,
        hasURL: !!audioURL,
      });
      return;
    }

    try {
      if (a.src !== audioURL) {
        a.src = audioURL;
        try {
          a.load();
        } catch {}
      }

      if (a.paused) {
        const p = a.play();
        if (p && typeof p.then === "function") {
          await p;
        }
        setIsPlaying(true);
      } else {
        a.pause();
        setIsPlaying(false);
      }
    } catch (err) {
      if (err?.name !== "AbortError" && err?.name !== "NotAllowedError") {
        console.error("Audio play error:", err);
      }
    }
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    console.log("handlePlayPause", audioRef);
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      try {
        audioRef.current.load();
      } catch {}
      const p = audioRef.current.play();
      if (p && typeof p.catch === "function") {
        p.catch((err) => {
          if (err?.name !== "AbortError" && err?.name !== "NotAllowedError") {
            console.error("Audio play error:", err);
          }
        });
      }
      if (p && typeof p.catch === "function") {
        p.catch((err) => {
          if (err?.name !== "AbortError" && err?.name !== "NotAllowedError") {
            console.error("Audio play error:", err);
          }
        });
      }
    }
  };

  const handleAudioEnd = () => {
    setIsPlaying(false);
  };

  useEffect(() => {
    return () => {
      // 오디오 정지 & 분리
      if (audioRef.current) {
        try {
          audioRef.current.pause();
        } catch {}
        audioRef.current.src = "";
      }
      // 객체 URL 정리
      if (audioURL) URL.revokeObjectURL(audioURL);
      // 마이크 스트림 정리
      if (mediaRecorderRef.current?.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop());
      }
    };
  }, [audioURL]);

  return (
    <div className="recording-container">
      <figure className="close-select-mode" onClick={onClose}>
        <img
          src={"/images/audiobook/guide-text-close.svg"}
          alt="guide-text-close"
        />
      </figure>
      <figure className="recording-title">
        <img
          src={getLocalizedAssetPath("/images/audiobook/recording-title.svg")}
          alt="recording-title"
        />
      </figure>

      {showGuideBox && (
        <div className="recording-guide-container">
          <figure
            className="guide-text-close"
            onClick={() => {
              setShowGuideBox(false);
            }}
          >
            <img
              src={"/images/audiobook/guide-text-close.svg"}
              alt="guide-text-close"
            />
          </figure>
          <div className="guide-title">
            <figure>
              <img src={"/images/utils/light.svg"} alt={t("guide.iconAlt")} />
            </figure>
            <h3>{t("guide.title")}</h3>
          </div>
          <p className="guide-desc">
            {selectedMode === "voice"
              ? t("guide.voiceDesc")
              : t("guide.customDesc")}
          </p>
        </div>
      )}

      <div className="sentence-box">
        <p className="origin-text">
          {sentence}
          <div className="text-stroke">{sentence}</div>
        </p>
      </div>

      <div className="recording-controls">
        {!isRecording && !hasRecorded && (
          <button className="record-btn start" onClick={startRecording}>
            <img src={"/images/audiobook/pause.svg"} alt="pause" />
          </button>
        )}
        {isRecording && (
          <button className="record-btn stop" onClick={stopRecording}>
            <img src={"/images/audiobook/recording.svg"} alt="pause" />
          </button>
        )}

        {hasRecorded && (
          <>
            <p className="recorded-complete-text">
              {t("status.recordedComplete")}
            </p>

            {/* 미리듣기 */}
            {audioURL && (
              <div className="preview-controls">
                <button className="preview-button" onClick={togglePlay}>
                  <img
                    src={
                      isPlaying
                        ? "/images/utils/pause.svg"
                        : "/images/utils/fast-forward.svg"
                    }
                    alt={
                      isPlaying
                        ? t("actions.stopPlayback")
                        : t("actions.previewListen")
                    }
                  />
                  <span>
                    {isPlaying
                      ? t("actions.stopPlayback")
                      : t("actions.previewListen")}
                  </span>
                </button>
                <audio
                  ref={audioRef}
                  src={audioURL}
                  onEnded={handleAudioEnd}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                />
              </div>
            )}

            <button
              className="complete-btn text-action retry"
              onClick={handleRetry}
            >
              <img
                src={"/images/utils/refresh.svg"}
                alt={t("actions.retryRecording")}
              />
              <span>{t("actions.retryRecording")}</span>
            </button>
            {/* {page + 1 === total ? (
                            <button
                                className="complete-btn record-complete"
                                onClick={handleSubmit}
                            >
                                <img
                                    src={
                                        '/images/audiobook/record-complete-btn.svg'
                                    }
                                    alt="record-complete-btn"
                                />
                            </button>
                        ) : (
                            
                            <button
                                className="complete-btn next"
                                onClick={handleSubmit}
                            >
                                <img
                                    src={
                                        '/images/audiobook/next-recording-btn.svg'
                                    }
                                    alt="next-recording-btn"
                                />
                            </button>
                        )} */}
            {isLast ? (
              <button
                className={"complete-btn text-action finish"}
                onClick={handleSubmit}
              >
                <img
                  src={"/images/utils/mic.svg"}
                  alt={
                    isCustom
                      ? t("actions.startAIVoice")
                      : t("actions.recordingComplete")
                  }
                />
                <span>{t("actions.recordingComplete")}</span>
              </button>
            ) : (
              <button
                className={`complete-btn text-action next`}
                onClick={handleSubmit}
              >
                <span>
                  {isCustom
                    ? t("actions.nextVoiceSample")
                    : t("actions.nextSentence")}
                </span>
                <img
                  src={"/images/utils/button/next.svg"}
                  alt={
                    isCustom
                      ? t("actions.nextVoiceSample")
                      : t("actions.nextSentence")
                  }
                />
              </button>
            )}
          </>
        )}
      </div>
      <div className="page-counter">
        {page + 1} / {total}
      </div>
    </div>
  );
};

export default Recording;
